import Draggable from "react-draggable";
import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    IconButton,
    Sheet,
    Stack,
    Tabs,
    Tab,
    tabClasses,
    TabList,
    TabPanel,
    Textarea,
    Theme,
    Typography,
    Badge,
} from "@mui/joy";
import {
    CloseOutlined,
    SendOutlined,
    ArchitectureOutlined,
    DescriptionOutlined,
} from "@mui/icons-material";
import { ApiDocumentTypes, ApiFragmentNode } from "../../shared/interfaces";
import { useQuery, useQueries } from "react-query";
import { PrimeQueryKeys } from "../../shared/constants";
import { FileApi } from "../../api/file";
import { useHasRichDocuments } from "../../hooks/configuration";
import { analyticsEvent, AnalyticsEvents } from "../../util/analytics";
import RelatedSpecItems from "./RelatedSpecItems";
import RelatedDrawingsItems from "./RelatedDrawingsItems";

const DRAWING_TYPE_MAPPING: Record<string, string> = {
    A: "Architectural",
    AD: "Architectural Demo",
    AS: "Architectural Slab Plan",
    C: "Civil",
    S: "Structural",
    SM: "Structural",
    SW: "Structural",
    SG: "Signage",
    P: "Plumbing",
    PM: "Plumbing",
    PW: "Plumbing",
    PD: "Plumbing Demo",
    E: "Electrical",
    EM: "Electrical",
    EW: "Electrical",
    ED: "Electrical Demo",
    EL: "Electrical Lighting",
    EP: "Electrical Power",
    M: "Mechanical",
    MM: "Mechanical",
    MW: "Mechanical",
    MD: "Mechanical Demo",
    MP: "Micropile",
    L: "Landscaping",
    G: "General",
    OSP: "Occupancy",
    T: "Telecom",
    TEL: "Telecom",
    F: "Fire Protection",
};

const styles = {
    askMarvinContainer: (theme: Theme) => ({
        background: "white",
        borderTop: "1px solid #eaeaea",
        borderBottomLeftRadius: theme.radius.md,
        borderBottomRightRadius: theme.radius.md,
        p: 1,
    }),
    containerSheet: {
        position: "absolute",
        top: "64px",
        right: "32px",
        width: 340,
        maxHeight: "calc(100vh - 128px)",
        background: "white",
        boxShadow: "md",
        borderRadius: "sm",
        zIndex: 9999,
        cursor: "default",
        border: "1px solid #2563EB",
        display: "flex",
        flexDirection: "column",
    },
    titleContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "move",
        px: 2,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
    },
    askMarvinInput: { flexGrow: 1, mr: 1 },
    relatedItemsContainer: { flexGrow: 1, overflowY: "auto" },
    relatedItemsTabList: {
        [`& .${tabClasses.root}`]: {
            fontWeight: "lg",
            flex: 1,
            color: "text.secondary",
            [`&.${tabClasses.selected}`]: {
                color: "primary.500",
            },
        },
    },
    relatedItemsTab: {
        display: "flex",
        flexDirection: "column",
        py: 2,
    },
    relatedItemsTabBadge: {
        "& .MuiBadge-badge": {
            right: -12,
        },
    },
    tabPanel: {
        p: 0,
    },
};

// Group related nodes by type (drawings or spec)
interface GroupedNodes {
    groupedByFileId: {
        drawings: ApiFragmentNode[];
        spec: ApiFragmentNode[];
    };
    drawingsByLetter: Record<string, ApiFragmentNode[]>;
}

export default function FloatingRelatedItems({
    fragmentNode,
    onClose,
    setFragmentNode,
    onMessageSent,
}: {
    fragmentNode: ApiFragmentNode;
    onClose: () => void;
    setFragmentNode: (node: ApiFragmentNode | null) => void;
    onMessageSent: (message: string, node: ApiFragmentNode) => void;
}) {
    useEffect(() => {
        analyticsEvent(AnalyticsEvents.VIEW_NODE);
    }, [fragmentNode.id]);

    const [open, setOpen] = useState(true);
    const [textAreaValue, setTextAreaValue] = useState("");

    // Fetch all related nodes for the current fragmentNode.
    const relatedNodesQueryFn = async () => {
        const nodes = await FileApi.getRelatedFragmentNodes(fragmentNode.fileId, fragmentNode.id);
        return nodes.filter((n) => {
            // Filter out things from the general CSI section or too far away
            return n.distance && n.distance >= 0.5 && !n.title.startsWith("01 ");
        });
    };

    const { data: relatedNodes = [], isFetching } = useQuery({
        queryKey: PrimeQueryKeys.relatedFragmentNodes(fragmentNode.fileId, fragmentNode.id),
        queryFn: relatedNodesQueryFn,
        enabled: open,
        staleTime: Infinity, // Never consider data stale
        cacheTime: 30 * 60 * 1000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

    // Fetch file information for all related nodes
    const filesQueries = useQueries(
        relatedNodes.map((node) => ({
            queryKey: [PrimeQueryKeys.file, node.fileId],
            queryFn: () => FileApi.getFile(node.fileId),
            enabled: open,
            staleTime: Infinity,
            cacheTime: 30 * 60 * 1000,
        })),
    );

    // Convert the array of file queries into a map of fileId -> file data
    const filesMap = React.useMemo(() => {
        return filesQueries.reduce<Record<string, any>>((acc, queryResult, index) => {
            if (queryResult.data) {
                const node = relatedNodes[index];
                acc[node.fileId] = queryResult.data;
            }
            return acc;
        }, {});
    }, [filesQueries, relatedNodes]);

    const grouped = React.useMemo<GroupedNodes>(() => {
        // Don't group until we have all file information
        const allFilesLoaded = relatedNodes.every((node) => filesMap[node.fileId]);

        if (!allFilesLoaded) {
            return {
                groupedByFileId: { drawings: [], spec: [] },
                drawingsByLetter: {},
            };
        }

        // First separate drawings and specs
        const grouped = relatedNodes.reduce<{
            drawings: ApiFragmentNode[];
            spec: ApiFragmentNode[];
        }>(
            (acc, node) => {
                const fileInfo = filesMap[node.fileId];
                // TODO(hp) -- this is too hacky
                const type = fileInfo.fileName.toLowerCase().includes("drawing")
                    ? "drawings"
                    : "spec";
                (acc[type] ??= []).push(node);
                return acc;
            },
            { drawings: [], spec: [] },
        );

        // Helper function to extract the title prefix. After trimming and uppercasing it, we try to find a prefix
        // consisting of one or more letters followed by a "-" or any whitespace character (e.g., "FP-" becomes "FP").
        // If no match is found, we default to the title's first character.
        function extractPrefix(title: string): string {
            const trimmedTitle = title.trim().toUpperCase();
            const match = trimmedTitle.match(/^([A-Z]+)(?:[-\s\d])/);
            const prefix = match ? match[1] : trimmedTitle.charAt(0);
            return DRAWING_TYPE_MAPPING[prefix] || prefix;
        }

        const drawingsByPrefix = grouped.drawings.reduce<Record<string, ApiFragmentNode[]>>(
            (acc, node) => {
                if (!node.title) return acc; // Skip nodes without a title

                const prefix = extractPrefix(node.title);
                if (!prefix) return acc; // Skip if prefix is not found

                (acc[prefix] ??= []).push(node);
                return acc;
            },
            {},
        );

        const sortedDrawingsByPrefixAndDistance = Object.fromEntries(
            Object.entries(drawingsByPrefix)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([prefix, nodes]) => [
                    prefix,
                    nodes.sort((a, b) => (b.distance || 0) - (a.distance || 0)),
                ]),
        );

        return {
            groupedByFileId: {
                drawings: grouped.drawings,
                spec: grouped.spec,
            },
            drawingsByLetter: sortedDrawingsByPrefixAndDistance,
        };
    }, [relatedNodes, filesMap]);

    const handleSubmit = useCallback(() => {
        const textVal = textAreaValue.trim();
        if (textVal.length > 0) {
            onMessageSent(textVal, fragmentNode);
            setTextAreaValue("");
        }
    }, [textAreaValue, fragmentNode, onMessageSent]);

    const handleClose = useCallback(() => {
        setOpen(false);
        onClose();
    }, [onClose]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit],
    );

    const hasRichDocuments = useHasRichDocuments();

    if (!open) {
        return null;
    }

    // We are loading if either the file query or the fragment node query is loading
    const isLoading = isFetching || filesQueries.some((q) => q.isFetching || q.isLoading);

    const hasGroupedDrawings = Object.keys(grouped.drawingsByLetter || {}).length > 0;

    return (
        <Draggable handle=".draggable-handle" bounds={{ right: 0, top: 0, bottom: 0 }}>
            <Sheet sx={styles.containerSheet}>
                <Box className="draggable-handle" sx={styles.titleContainer}>
                    <Typography level="title-md" color="primary">
                        {fragmentNode.title}
                    </Typography>
                    <IconButton onClick={handleClose} size="sm">
                        <CloseOutlined />
                    </IconButton>
                </Box>

                <Box sx={styles.relatedItemsContainer}>
                    <Tabs
                        aria-label="Related files"
                        defaultValue={
                            fragmentNode.documentType === ApiDocumentTypes.Drawing
                                ? "spec"
                                : "drawings"
                        }
                    >
                        <TabList size="sm" sx={styles.relatedItemsTabList}>
                            {hasRichDocuments && (
                                <Tab value="drawings" sx={styles.relatedItemsTab}>
                                    <Badge
                                        badgeContent={
                                            hasGroupedDrawings
                                                ? Object.values(grouped.drawingsByLetter).reduce(
                                                      (sum, nodes) => sum + nodes.length,
                                                      0,
                                                  )
                                                : 0
                                        }
                                        color="neutral"
                                        size="sm"
                                        sx={styles.relatedItemsTabBadge}
                                    >
                                        <Typography
                                            level="body-sm"
                                            startDecorator={<ArchitectureOutlined />}
                                        >
                                            Drawings
                                        </Typography>
                                    </Badge>
                                </Tab>
                            )}

                            <Tab value="spec" sx={styles.relatedItemsTab}>
                                <Badge
                                    badgeContent={grouped.groupedByFileId["spec"]?.length || 0}
                                    color="neutral"
                                    size="sm"
                                    sx={styles.relatedItemsTabBadge}
                                >
                                    <Typography
                                        level="body-sm"
                                        startDecorator={<DescriptionOutlined />}
                                    >
                                        Spec
                                    </Typography>
                                </Badge>
                            </Tab>
                        </TabList>
                        <TabPanel value="drawings" sx={styles.tabPanel}>
                            <RelatedDrawingsItems
                                drawingsByLetter={grouped.drawingsByLetter}
                                setFragmentNode={setFragmentNode}
                                isLoading={isLoading}
                                DRAWING_TYPE_MAPPING={DRAWING_TYPE_MAPPING}
                                styles={styles}
                            />
                        </TabPanel>
                        <TabPanel value="spec" sx={styles.tabPanel}>
                            <RelatedSpecItems
                                specNodes={grouped.groupedByFileId["spec"]}
                                setFragmentNode={setFragmentNode}
                                isLoading={isLoading}
                            />
                        </TabPanel>
                    </Tabs>
                </Box>
                <Box sx={styles.askMarvinContainer}>
                    <Stack direction="row" alignItems="center">
                        <Textarea
                            variant="outlined"
                            placeholder="Ask Marvin ..."
                            minRows={1}
                            size="sm"
                            value={textAreaValue}
                            onChange={(e) => setTextAreaValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            sx={styles.askMarvinInput}
                        />
                        <IconButton
                            size="sm"
                            color="neutral"
                            variant="plain"
                            onClick={handleSubmit}
                        >
                            <SendOutlined />
                        </IconButton>
                    </Stack>
                </Box>
            </Sheet>
        </Draggable>
    );
}
